import toast from "react-hot-toast";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { logger } from "@artis/logger";
import { graphql, readFragment } from "../../graphql";
import { nanoid } from "nanoid";
import { useMemo } from "react";
import { useNewFeatures } from "../../sanity/queries";
import dayjs from "dayjs";
import { useGraphQLClient } from "@package/graphql/react";
import type { GraphQLClient } from "@package/graphql";
import type { ViewedAnnouncementsFragmentFragment } from "../../__generated__/gql/graphql";
import { useUserId } from "../../context/auth/hooks";

export const ViewedAnnouncementsFragment = graphql(`
  fragment ViewedAnnouncementsFragment on viewed_announcements {
		id
		user_id
		announcement_id
		title
		viewed_at
		dismissed_at
	}
`);

export type AnnouncementStateRow = ViewedAnnouncementsFragmentFragment;

export const viewedAnnouncementsQuery = graphql(`
	query ViewedAnnouncements($userId: String!) {
		viewed_announcements(where: { user_id: { _eq: $userId } }) {
			...ViewedAnnouncementsFragment
		}
	}
`);

export const insertAnnouncementStateMutation = graphql(`
	mutation InsertAnnouncementState(
		$id: String!
		$userId: String!
		$announcementId: String!
		$title: String!
		$viewedAt: timestamptz
		$dismissedAt: timestamptz
	) {
		insert_viewed_announcements_one(
			object: {
				id: $id
				user_id: $userId
				announcement_id: $announcementId
				title: $title
				viewed_at: $viewedAt
				dismissed_at: $dismissedAt
			}
		) {
			...ViewedAnnouncementsFragment
		}
	}
`);

export const updateAnnouncementStateMutation = graphql(`
	mutation UpdateAnnouncementState($id: String!, $set: viewed_announcements_set_input!) {
		update_viewed_announcements_by_pk(pk_columns: { id: $id }, _set: $set) {
			...ViewedAnnouncementsFragment
		}
	}
`);

export function useViewedAnnouncements() {
  const userId = useUserId();
  const hasura = useGraphQLClient();

  return useQuery({
    queryKey: ["viewedAnnouncements", userId],
    queryFn: async () => {
      const res = await hasura.execute(viewedAnnouncementsQuery, { userId });
      const data = readFragment(ViewedAnnouncementsFragment, res?.data?.viewed_announcements ?? []);
      return data;
    },
  });
}

type AnnouncementStatePatch = { viewed_at?: string; dismissed_at?: string };

/** No unique (user_id, announcement_id) — insert vs update is decided from `existingRows`. */
async function upsertAnnouncementState(
  userId: string,
  announcement: { announcement_id: string; title: string },
  patch: AnnouncementStatePatch,
  existingRows: readonly AnnouncementStateRow[],
  hasura: GraphQLClient,
  queryClient: QueryClient,
) {
  const existing = existingRows.find((row) => row.announcement_id === announcement.announcement_id);

  if (existing) {
    const noopViewed = patch.viewed_at == null || existing.viewed_at != null;
    const noopDismissed = patch.dismissed_at == null || existing.dismissed_at != null;
    if (noopViewed && noopDismissed) return;

    const set: { viewed_at?: string; dismissed_at?: string } = {};
    if (patch.viewed_at != null && existing.viewed_at == null) set.viewed_at = patch.viewed_at;
    if (patch.dismissed_at != null && existing.dismissed_at == null) set.dismissed_at = patch.dismissed_at;

    await hasura.execute(updateAnnouncementStateMutation, {
      id: existing.id,
      set,
    });
  } else {
    await hasura.execute(insertAnnouncementStateMutation, {
      id: nanoid(),
      userId,
      announcementId: announcement.announcement_id,
      title: announcement.title,
      viewedAt: patch.viewed_at ?? null,
      dismissedAt: patch.dismissed_at ?? null,
    });
  }

  await queryClient.invalidateQueries({ queryKey: ["viewedAnnouncements", userId] });
}

export type WhatsNewFeature = {
  _id?: string | null;
  title?: string | null;
  description?: string | null;
  bodyRaw?: unknown;
  publishedAt?: string | null;
};

export const WHATS_NEW_MAX_AGE_MONTHS = 6;

export const getRecentFeatures = (features: ReadonlyArray<WhatsNewFeature> | undefined): WhatsNewFeature[] => {
  const cutoff = dayjs().subtract(WHATS_NEW_MAX_AGE_MONTHS, "month");
  return (features ?? [])
    .filter((feature) => feature.publishedAt && dayjs(feature.publishedAt).isAfter(cutoff))
    .slice()
    .sort((a, b) => (dayjs(a?.publishedAt).isAfter(dayjs(b?.publishedAt)) ? -1 : 1));
};

export async function markAllAnnouncementsAsRead(
  userId: string,
  features: ReadonlyArray<WhatsNewFeature>,
  existingRows: readonly AnnouncementStateRow[],
  hasura: GraphQLClient,
  queryClient: QueryClient,
) {
  try {
    if (!userId) {
      throw new Error("Invalid userId");
    }

    const viewedAt = new Date().toISOString();
    const unread = getRecentFeatures(features)
      .filter(
        (feature): feature is WhatsNewFeature & { _id: string; title: string } =>
          Boolean(feature._id && feature.title) &&
          !existingRows.some((row) => row.announcement_id === feature._id && row.viewed_at != null),
      )
      .map((feature) => ({ announcement_id: feature._id, title: feature.title }));

    if (unread.length === 0) {
      return;
    }

    await Promise.all(
      unread.map((feature) =>
        upsertAnnouncementState(
          userId,
          feature,
          { viewed_at: viewedAt, dismissed_at: viewedAt },
          existingRows,
          hasura,
          queryClient,
        ),
      ),
    );

    logger.debug("Marked all announcements as read", { count: unread.length });
  } catch (error) {
    toast.error("An error occurred while marking announcements as read. Please try again.", {
      position: "bottom-left",
    });
    logger.error("Error marking all announcements as read", { error, userId });
    throw error;
  }
}

export async function insertViewedAnnouncement(
  userId: string,
  announcement: {
    announcement_id: string;
    title: string;
    viewed_at: string;
  },
  existingRows: readonly AnnouncementStateRow[],
  hasura: GraphQLClient,
  queryClient: QueryClient,
) {
  try {
    if (!userId || !announcement) {
      throw new Error("Invalid userId or announcement");
    }

    await upsertAnnouncementState(
      userId,
      announcement,
      { viewed_at: announcement.viewed_at, dismissed_at: announcement.viewed_at },
      existingRows,
      hasura,
      queryClient,
    );

    logger.debug("Marked announcement as viewed", { announcement });
  } catch (error) {
    toast.error("An error occurred while saving the viewed announcement. Please try again.", {
      position: "bottom-left",
    });
    logger.error("Error inserting viewed announcement", {
      error,
      userId,
      announcement,
    });
    throw error;
  }
}

export async function dismissAnnouncementToast(
  userId: string,
  announcement: { announcement_id: string; title: string },
  existingRows: readonly AnnouncementStateRow[],
  hasura: GraphQLClient,
  queryClient: QueryClient,
) {
  try {
    if (!userId || !announcement) {
      throw new Error("Invalid userId or announcement");
    }

    await upsertAnnouncementState(
      userId,
      announcement,
      { dismissed_at: new Date().toISOString() },
      existingRows,
      hasura,
      queryClient,
    );

    logger.debug("Dismissed announcement toast", { announcement });
  } catch (error) {
    logger.error("Error dismissing announcement toast", { error, userId, announcement });
  }
}

export async function dismissAnnouncementToasts(
  userId: string,
  announcements: ReadonlyArray<{ announcement_id: string; title: string }>,
  existingRows: readonly AnnouncementStateRow[],
  hasura: GraphQLClient,
  queryClient: QueryClient,
) {
  await Promise.all(
    announcements.map((announcement) =>
      dismissAnnouncementToast(userId, announcement, existingRows, hasura, queryClient),
    ),
  );
}

export const useUnviewedFeatures = (limit: number) => {
  const features = useNewFeatures();
  const announcements = useViewedAnnouncements();
  return (features.data?.allNewFeature ?? [])
    .map((feature) => {
      const row = announcements.data?.find((x) => x.announcement_id === feature._id);
      return {
        ...feature,
        viewed: row?.viewed_at != null,
      };
    })
    .sort((a, b) => (dayjs(a?.publishedAt).isAfter(dayjs(b?.publishedAt)) ? -1 : 1))
    .slice(0, limit);
};

export const useToastableAnnouncements = (): Array<WhatsNewFeature & { _id: string; title: string }> => {
  const features = useNewFeatures();
  const viewedAnnouncements = useViewedAnnouncements();
  const seenIds = useMemo(
    () => new Set((viewedAnnouncements.data ?? []).map((row) => row.announcement_id)),
    [viewedAnnouncements.data],
  );

  return useMemo(
    () =>
      getRecentFeatures(features.data?.allNewFeature).filter(
        (feature): feature is WhatsNewFeature & { _id: string; title: string } =>
          Boolean(feature._id && feature.title) && !seenIds.has(feature._id as string),
      ),
    [features.data?.allNewFeature, seenIds],
  );
};
